/**
 * Device-approval state machine for the web app (SPEC §20). Pure reducer; the React gate in
 * apps/web drives it with API results. Phases:
 *
 * - `registering`: POST /v1/devices in flight (fresh or re-attaching with the stored secret).
 * - `approved`: the app may run. Also the phase whenever the server does not enforce approval.
 * - `code`: first device; the user types the 6-digit code from the helm-api log.
 * - `trusted`: a later device; approve it from a device already in use. The gate polls.
 * - `failed`: registration failed; offer a retry.
 *
 * The gate polls the device list in `code` and `trusted`: another device may approve this one,
 * or another device may take the first-device code, which turns `code` into `trusted`.
 */

export type DeviceApproval = "approved" | "code_required" | "trusted_device_required";

export type DeviceInfo = { id: string; name: string; platform: string };

export type CodeError =
  | { kind: "wrong_code" }
  | { kind: "rate_limited" }
  | { kind: "invalid" }
  | { kind: "other"; message: string };

export type ApprovalState =
  | { phase: "registering" }
  | { phase: "approved"; device: DeviceInfo | null }
  | { phase: "code"; device: DeviceInfo; submitting: boolean; error: CodeError | null; attempts: number }
  | { phase: "trusted"; device: DeviceInfo }
  | { phase: "failed"; message: string };

export type ApprovalEvent =
  | { type: "registered"; device: DeviceInfo; approval: DeviceApproval; enforced: boolean }
  | { type: "register_failed"; message: string }
  | { type: "code_submitted"; code: string }
  | { type: "code_accepted" }
  | { type: "code_rejected"; status: number; code: string; message: string }
  /** From polling GET /v1/devices. */
  | { type: "poll"; approved: boolean; otherApproved: boolean }
  /** Any API call answered 403 DEVICE_NOT_APPROVED (e.g. approval revoked on the server). */
  | { type: "not_approved" }
  | { type: "retry" };

export const INITIAL_APPROVAL: ApprovalState = { phase: "registering" };

/** How often the gate re-checks while waiting (ms). */
export const APPROVAL_POLL_MS = 3000;

export function isValidCode(code: string): boolean {
  return /^\d{6}$/.test(code);
}

function codeError(status: number, code: string, message: string): CodeError {
  if (status === 429 || code === "RATE_LIMITED") return { kind: "rate_limited" };
  if (status === 403 && code === "FORBIDDEN") return { kind: "wrong_code" };
  return { kind: "other", message };
}

export function reduceApproval(state: ApprovalState, event: ApprovalEvent): ApprovalState {
  switch (event.type) {
    case "registered": {
      if (!event.enforced || event.approval === "approved")
        return { phase: "approved", device: event.device };
      return event.approval === "code_required"
        ? { phase: "code", device: event.device, submitting: false, error: null, attempts: 0 }
        : { phase: "trusted", device: event.device };
    }
    case "register_failed":
      return { phase: "failed", message: event.message };
    case "code_submitted":
      if (state.phase !== "code" || state.submitting) return state;
      if (!isValidCode(event.code)) return { ...state, error: { kind: "invalid" } };
      return { ...state, submitting: true, error: null };
    case "code_accepted":
      return state.phase === "code" ? { phase: "approved", device: state.device } : state;
    case "code_rejected":
      if (state.phase !== "code") return state;
      return {
        ...state,
        submitting: false,
        attempts: state.attempts + 1,
        error: codeError(event.status, event.code, event.message),
      };
    case "poll":
      if (state.phase !== "code" && state.phase !== "trusted") return state;
      if (event.approved) return { phase: "approved", device: state.device };
      // Another device became the trusted one: the first-device code no longer applies.
      if (state.phase === "code" && event.otherApproved && !state.submitting) {
        return { phase: "trusted", device: state.device };
      }
      return state;
    case "not_approved":
      return state.phase === "approved" ? { phase: "registering" } : state;
    case "retry":
      return state.phase === "failed" || state.phase === "code" ? { phase: "registering" } : state;
  }
}

/** Whether the app behind the gate may run. */
export function isApproved(state: ApprovalState): boolean {
  return state.phase === "approved";
}

export function codeErrorMessage(err: CodeError): string {
  switch (err.kind) {
    case "invalid":
      return "Enter the 6 digits from the helm-api log.";
    case "wrong_code":
      return "That code did not match. Use the most recent FIRST-DEVICE APPROVAL CODE line in the log. After 5 wrong tries the code stops working; get a new one below once 10 minutes have passed.";
    case "rate_limited":
      return "Too many attempts. Code entry is paused for up to an hour for this account.";
    case "other":
      return err.message;
  }
}
